import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Volume2 } from "lucide-react";
import { gameDiff } from "../gradeProfile";
import { sfx } from "../sfx";

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);
export const say = (w) => {
  try {
    const u = new SpeechSynthesisUtterance(w);
    u.lang = "en-US"; u.rate = 0.9;
    speechSynthesis.cancel(); speechSynthesis.speak(u);
  } catch { /* speech not available */ }
};

const Chip = ({ children, onClick, style, testid, disabled }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={disabled}
    className="play-btn text-white text-sm disabled:opacity-50 transition-transform active:scale-95"
    style={style}>{children}</button>
);

const Progress = ({ i, total, score }) => (
  <p className="text-center text-sm text-white/70 mt-5">{i}/{total} · ✅ {score}</p>
);

/* ---------- match: word ↔ scrambled letters ---------- */
const scramble = (w) => {
  const s = shuffle(w.replace(/\s/g, "").split("")).join("");
  return s.toLowerCase() === w.replace(/\s/g, "").toLowerCase() ? scramble(w) : s;
};

const Match = ({ words, diff, onDone }) => {
  const n = diff?.words || 5;
  const pairs = useMemo(() => words.slice(0, n).map((w) => ({ w, s: scramble(w) })), [words, n]);
  const [left] = useState(() => shuffle(pairs));
  const [right] = useState(() => shuffle(pairs));
  const [sel, setSel] = useState(null);
  const [ok, setOk] = useState([]);
  const [wrong, setWrong] = useState(0);

  const pick = (side, p) => {
    if (ok.includes(p.w)) return;
    if (!sel || sel.side === side) return setSel({ side, w: p.w });
    if (sel.w === p.w) {
      const next = [...ok, p.w];
      setOk(next); setSel(null); say(p.w);
      if (next.length === pairs.length) onDone(pairs.length, pairs.length + wrong);
    } else { toast.error("❌"); setWrong((n) => n + 1); setSel(null); }
  };

  return (
    <div className="grid grid-cols-2 gap-4" data-testid="task-match">
      {[["left", left], ["right", right]].map(([side, list]) => (
        <div key={side} className="space-y-3">
          <div className="text-xs font-bold text-center text-white/50">
            {side === "left" ? "الكلمة" : "حروف مبعثرة"}</div>
          {list.map((p) => (
            <Chip key={side + p.w} testid={`match-${side}-${p.w}`} onClick={() => pick(side, p)}
              style={{
                background: ok.includes(p.w) ? "#38B000"
                  : sel?.w === p.w && sel.side === side ? "var(--play-accent)" : "var(--play-secondary)",
                width: "100%",
              }}>{side === "left" ? p.w : p.s}</Chip>
          ))}
        </div>
      ))}
    </div>
  );
};

/* ---------- missing letter ---------- */
const Missing = ({ words, diff, onDone }) => {
  const list = useMemo(() => words.slice(0, diff?.words || 5), [words, diff]);
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const w = list[i] || "";
  const hidden = useMemo(() => {
    const idx = Math.max(1, Math.floor(w.replace(/\s/g, "").length / 2));
    return { idx, char: w[idx] };
  }, [w]);
  const options = useMemo(() => shuffle([hidden.char, ...shuffle("abcdefghijklmnopqrstuvwxyz".split(""))
    .filter((c) => c !== hidden.char).slice(0, (diff?.letterOptions || 4) - 1)]), [hidden, diff]);

  const pick = (c) => {
    const good = c === hidden.char;
    if (good) { setScore((s) => s + 1); say(w); toast.success("✅"); } else toast.error(`❌ ${w}`);
    if (i + 1 < list.length) setI(i + 1);
    else onDone(score + (good ? 1 : 0), list.length);
  };

  return (
    <div className="text-center" data-testid="task-missing">
      <div className="text-4xl font-black tracking-[0.35em]" dir="ltr">
        {w.split("").map((c, k) => (k === hidden.idx ? "_" : c))}</div>
      <div className="grid grid-cols-4 gap-3 mt-8">
        {options.map((c) => (
          <Chip key={c} testid={`missing-opt-${c}`} onClick={() => pick(c)}
            style={{ background: "var(--play-primary)", fontSize: "1.25rem" }}>{c}</Chip>
        ))}
      </div>
      <Progress i={i + 1} total={list.length} score={score} />
    </div>
  );
};

/* ---------- memory ---------- */
const Memory = ({ words, diff, onDone }) => {
  const pairsCount = Math.min(diff?.memoryPairs || 4, words.length);
  const deck = useMemo(() => shuffle([...words.slice(0, pairsCount), ...words.slice(0, pairsCount)]
    .map((w, i) => ({ id: i, w }))), [words, pairsCount]);
  const [open, setOpen] = useState([]);
  const [found, setFound] = useState([]);
  const [tries, setTries] = useState(0);

  const flip = (card) => {
    if (found.includes(card.w) || open.find((o) => o.id === card.id) || open.length === 2) return;
    const next = [...open, card];
    setOpen(next);
    if (next.length === 2) {
      setTries((n) => n + 1);
      setTimeout(() => {
        if (next[0].w === next[1].w) {
          const f = [...found, next[0].w];
          setFound(f); say(next[0].w);
          if (f.length === Math.min(pairsCount, words.length)) onDone(f.length, Math.max(f.length, tries + 1));
        }
        setOpen([]);
      }, 650);
    }
  };

  return (
    <div className="grid grid-cols-4 gap-3" data-testid="task-memory">
      {deck.map((c) => {
        const shown = found.includes(c.w) || open.find((o) => o.id === c.id);
        return (
          <button key={c.id} data-testid={`memory-card-${c.id}`} onClick={() => flip(c)}
            className="h-24 rounded-2xl border-2 font-bold transition-transform active:scale-95"
            style={{
              background: shown ? (found.includes(c.w) ? "#38B000" : "var(--play-secondary)") : "#0A2540",
              color: "#fff", borderColor: "rgba(255,255,255,.2)",
            }}>{shown ? c.w : "?"}</button>
        );
      })}
    </div>
  );
};

/* ---------- spelling from audio ---------- */
const Spell = ({ words, diff, onDone }) => {
  const list = useMemo(() => words.slice(0, diff?.spellWords || 4), [words, diff]);
  const [i, setI] = useState(0);
  const [val, setVal] = useState("");
  const [score, setScore] = useState(0);
  const w = list[i] || "";
  useEffect(() => { if (w) say(w); }, [w]);

  const check = (e) => {
    e.preventDefault();
    const good = val.trim().toLowerCase() === w.toLowerCase();
    if (good) { setScore((s) => s + 1); toast.success("✅"); } else toast.error(`❌ ${w}`);
    setVal("");
    if (i + 1 < list.length) setI(i + 1); else onDone(score + (good ? 1 : 0), list.length);
  };

  return (
    <form onSubmit={check} className="text-center space-y-4" data-testid="task-spell">
      <button type="button" onClick={() => say(w)} data-testid="spell-listen-btn"
        className="play-btn text-white inline-flex items-center gap-2 text-lg"
        style={{ background: "var(--play-secondary)" }}><Volume2 />اسمع الكلمة</button>
      <input className="input max-w-xs mx-auto text-center text-xl" dir="ltr" value={val}
        data-testid="spell-input" onChange={(e) => setVal(e.target.value)} placeholder="type the word" />
      <button className="play-btn text-white block mx-auto" data-testid="spell-submit-btn"
        style={{ background: "var(--play-primary)" }}>OK</button>
      <Progress i={i + 1} total={list.length} score={score} />
    </form>
  );
};

/* ---------- listening: hear the word, pick the meaning ---------- */
const Listening = ({ payload, words, onDone }) => {
  const entries = (payload?.entries || []).filter((e) => e.ar);
  const rounds = useMemo(() => shuffle(entries).slice(0, 4), [payload]);
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const cur = rounds[i];
  const options = useMemo(() => (cur
    ? shuffle([cur, ...shuffle(entries.filter((e) => e.word !== cur.word)).slice(0, 3)]) : []), [cur]);

  useEffect(() => { if (cur) say(cur.word); }, [cur]);

  if (!rounds.length) return <Missing words={words} onDone={onDone} />;

  const pick = (o) => {
    const good = o.word === cur.word;
    if (good) { setScore((s) => s + 1); toast.success(`✅ ${cur.word}`); } else toast.error(`❌ ${cur.word}`);
    if (i + 1 < rounds.length) setI(i + 1); else onDone(score + (good ? 1 : 0), rounds.length);
  };

  return (
    <div data-testid="task-listening">
      <button onClick={() => say(cur.word)} data-testid="listening-play-btn"
        className="play-btn text-white mx-auto flex items-center gap-2 text-lg"
        style={{ background: "var(--play-secondary)" }}><Volume2 />اسمع الكلمة</button>
      <div className="grid grid-cols-2 gap-3 mt-6">
        {options.map((o) => (
          <Chip key={o.word} testid={`listening-opt-${o.word}`} onClick={() => pick(o)}
            style={{ background: "var(--play-primary)" }}>{o.ar}</Chip>
        ))}
      </div>
      <Progress i={i + 1} total={rounds.length} score={score} />
    </div>
  );
};

/* ---------- meanings: word ↔ Arabic meaning ---------- */
const Meanings = ({ payload, words, diff, onDone }) => {
  const entries = (payload?.entries || []).filter((e) => e.ar).slice(0, diff?.words || 5);
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const cur = entries[i];
  const options = useMemo(() => (cur
    ? shuffle([cur, ...shuffle(entries.filter((e) => e.word !== cur.word))
      .slice(0, (diff?.meaningOptions || 4) - 1)]) : []), [cur, diff]);

  if (entries.length < 2) return <Match words={words} onDone={onDone} />;

  const pick = (o) => {
    const good = o.word === cur.word;
    if (good) { setScore((s) => s + 1); say(cur.word); toast.success("✅"); }
    else toast.error(`❌ ${cur.ar}`);
    if (i + 1 < entries.length) setI(i + 1); else onDone(score + (good ? 1 : 0), entries.length);
  };

  return (
    <div data-testid="task-meanings">
      <p className="text-center text-white/70 text-sm">ما معنى هذه الكلمة؟</p>
      <div className="text-4xl font-black text-center mt-3" dir="ltr">{cur.word}</div>
      <div className="grid gap-3 mt-6">
        {options.map((o) => (
          <Chip key={o.word} testid={`meanings-opt-${o.word}`} onClick={() => pick(o)}
            style={{ background: "var(--play-secondary)" }}>{o.ar}</Chip>
        ))}
      </div>
      <Progress i={i + 1} total={entries.length} score={score} />
    </div>
  );
};

/* ---------- mini quiz / reading questions ---------- */
const QuestionSet = ({ questions, header, onDone, testid }) => {
  const list = (questions || []).filter((q) => Array.isArray(q.options) && q.options.length >= 2);
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const q = list[i];

  if (!list.length) return <p className="text-white/60 text-center">لا توجد أسئلة — حاول مرة أخرى</p>;

  const answerIndex = typeof q.answer === "number"
    ? q.answer : q.options.findIndex((o) => o === q.answer);

  const pick = (k) => {
    const good = k === answerIndex;
    if (good) { setScore((s) => s + 1); toast.success("✅ إجابة صحيحة"); }
    else toast.error(`❌ الصحيح: ${q.options[answerIndex]}`);
    if (i + 1 < list.length) setI(i + 1); else onDone(score + (good ? 1 : 0), list.length);
  };

  return (
    <div data-testid={testid}>
      {header}
      <div className="font-bold mt-4" dir="ltr">{i + 1}. {q.question}</div>
      <div className="grid gap-3 mt-4">
        {q.options.map((o, k) => (
          <Chip key={k} testid={`${testid}-opt-${k}`} onClick={() => pick(k)}
            style={{ background: "var(--play-primary)" }}>{o}</Chip>
        ))}
      </div>
      <Progress i={i + 1} total={list.length} score={score} />
    </div>
  );
};

const MiniQuiz = ({ payload, words, onDone }) => (
  (payload?.questions || []).length
    ? <QuestionSet questions={payload.questions} onDone={onDone} testid="task-miniquiz"
        header={<p className="text-center text-white/70 text-sm">أجب على الأسئلة</p>} />
    : <Match words={words} onDone={onDone} />
);

const Reading = ({ payload, words, onDone }) => {
  const [read, setRead] = useState(false);
  if (!payload?.text || !(payload.questions || []).length) return <Match words={words} onDone={onDone} />;
  if (!read) {
    return (
      <div data-testid="task-reading">
        <h3 className="text-2xl font-black" dir="ltr">{payload.title}</h3>
        {payload.title_ar && <div className="text-sm text-white/70 mt-1">{payload.title_ar}</div>}
        <p className="mt-4 text-lg leading-[2.1] text-white/90 whitespace-pre-line" dir="ltr">
          {payload.text}</p>
        {payload.summary_ar && <p className="mt-4 text-sm text-white/70">💡 {payload.summary_ar}</p>}
        <button onClick={() => setRead(true)} data-testid="reading-continue-btn"
          className="play-btn text-white mt-6 w-full" style={{ background: "var(--play-accent)" }}>
          قرأت النص — إلى الأسئلة</button>
      </div>
    );
  }
  return <QuestionSet questions={payload.questions} onDone={onDone} testid="task-reading-q"
    header={<p className="text-center text-white/70 text-sm">أسئلة عن النص</p>} />;
};

/* ---------- word hunt ---------- */
const Hunt = ({ words, diff, onDone }) => {
  const minLen = diff?.minWordLen || 4;
  const target = useMemo(() => (words.find((w) => w.length >= minLen && !w.includes(" ")) || "study")
    .toLowerCase(), [words, minLen]);
  const size = diff?.huntSize || 8;
  const grid = useMemo(() => {
    const g = Array.from({ length: size }, () => Array.from({ length: size },
      () => "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)]));
    const row = Math.floor(Math.random() * size);
    const start = Math.floor(Math.random() * (size - target.length + 1));
    target.split("").forEach((c, k) => { g[row][start + k] = c; });
    return g;
  }, [target]);
  const [picked, setPicked] = useState([]);
  const [misses, setMisses] = useState(0);

  const click = (r, c) => {
    const key = `${r}-${c}`;
    if (picked.includes(key)) return;
    const next = [...picked, key];
    const word = next.map((k) => {
      const [rr, cc] = k.split("-").map(Number);
      return grid[rr][cc];
    }).join("");
    if (!target.startsWith(word)) {
      toast.error("❌"); setPicked([]); setMisses((m) => m + 1);
      if (misses + 1 >= 4) onDone(1, 3);
      return;
    }
    setPicked(next);
    if (word === target) { say(target); onDone(3, 3); }
  };

  return (
    <div data-testid="task-hunt" dir="ltr">
      <p className="text-center font-bold mb-4">ابحث عن: <span className="text-xl">{target}</span></p>
      <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${size}, minmax(0,1fr))` }}>
        {grid.map((row, r) => row.map((ch, c) => (
          <button key={`${r}-${c}`} data-testid={`hunt-cell-${r}-${c}`} onClick={() => click(r, c)}
            className="aspect-square rounded-lg border-2 font-bold text-sm"
            style={{
              borderColor: "rgba(255,255,255,.2)",
              background: picked.includes(`${r}-${c}`) ? "var(--play-secondary)" : "rgba(255,255,255,.12)",
              color: "#fff",
            }}>{ch}</button>
        )))}
      </div>
    </div>
  );
};

/* ---------- sentence builder (words of the lesson) ---------- */
const Builder = ({ payload, words, diff, onDone }) => {
  const sentences = useMemo(() => {
    const w = words.filter(Boolean);
    const easy = [
      `I like the ${w[0] || "book"} in my ${w[1] || "school"}`,
      `We can see a ${w[2] || "friend"} every ${w[3] || "day"}`,
      `My ${w[4] || "teacher"} has a big ${w[5] || "idea"}`,
    ];
    const hard = [
      `Yesterday my ${w[0] || "friend"} and I studied the new ${w[1] || "lesson"} together at school`,
      `If you practise the ${w[2] || "words"} every day you will speak English better`,
      `The ${w[3] || "teacher"} told us that reading a short ${w[4] || "story"} improves our vocabulary`,
      `Although the ${w[5] || "test"} was difficult most students answered the questions correctly`,
    ];
    const pool = (diff?.grade || 3) <= 6 ? easy : hard;
    return pool.slice(0, diff?.builderRounds || 3);
  }, [words, diff]);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const target = sentences[idx];
  const [pool, setPool] = useState(() => shuffle(sentences[0].split(" ")));
  const [built, setBuilt] = useState([]);

  const take = (w, i) => { setBuilt((b) => [...b, w]); setPool((p) => p.filter((_, k) => k !== i)); };
  const undo = () => {
    if (!built.length) return;
    setPool((p) => [...p, built[built.length - 1]]);
    setBuilt((b) => b.slice(0, -1));
  };
  const check = () => {
    const good = built.join(" ") === target;
    if (good) { setScore((s) => s + 1); say(target); toast.success("✅"); }
    else toast.error(`❌ ${target}`);
    const n = idx + 1;
    if (n < sentences.length) {
      setIdx(n); setBuilt([]); setPool(shuffle(sentences[n].split(" ")));
    } else onDone(score + (good ? 1 : 0), sentences.length);
  };

  return (
    <div data-testid="task-builder">
      <p className="text-sm text-white/70 text-center">رتّب الكلمات لتكوين جملة صحيحة ({idx + 1}/{sentences.length})</p>
      <div dir="ltr" data-testid="builder-sentence"
        className="min-h-20 rounded-2xl border-2 border-dashed border-white/15 p-4 mt-4 flex flex-wrap gap-2">
        {built.map((w, k) => (
          <span key={k} className="rounded-lg px-3 py-1 text-white font-bold"
            style={{ background: "var(--play-secondary)" }}>{w}</span>
        ))}
      </div>
      <div dir="ltr" className="flex flex-wrap gap-2 mt-5 justify-center">
        {pool.map((w, k) => (
          <Chip key={`${w}-${k}`} testid={`builder-word-${w}-${k}`} onClick={() => take(w, k)}
            style={{ background: "var(--play-primary)" }}>{w}</Chip>
        ))}
      </div>
      <div className="flex gap-2 mt-6">
        <button onClick={undo} data-testid="builder-undo-btn"
          className="pro-btn bg-white/12 text-white border border-white/20 flex-1">↺</button>
        <button onClick={check} disabled={!!pool.length} data-testid="builder-check-btn"
          className="play-btn text-white flex-1 disabled:opacity-50"
          style={{ background: "var(--play-accent)" }}>OK</button>
      </div>
    </div>
  );
};

// ---------- Crossword: numbered clues, tap a clue to fill its squares ----------
const buildCrossword = (pairs) => {
  const clean = pairs.filter((p) => /^[a-zA-Z]{3,9}$/.test(p.word)).slice(0, 5);
  if (clean.length < 2) return null;
  const placed = [];
  const first = clean[0].word.toLowerCase();
  placed.push({ ...clean[0], word: first, row: 4, col: 1, dir: "across" });
  for (const p of clean.slice(1)) {
    const w = p.word.toLowerCase();
    let done = false;
    for (const ex of placed) {
      for (let i = 0; i < ex.word.length && !done; i++) {
        const j = w.indexOf(ex.word[i]);
        if (j < 0) continue;
        const cand = ex.dir === "across"
          ? { row: ex.row - j, col: ex.col + i, dir: "down" }
          : { row: ex.row + i, col: ex.col - j, dir: "across" };
        if (cand.row < 0 || cand.col < 0) continue;
        const cells = [...w].map((ch, k) => ({
          r: cand.dir === "across" ? cand.row : cand.row + k,
          c: cand.dir === "across" ? cand.col + k : cand.col, ch,
        }));
        const conflict = cells.some((cell) => placed.some((q) => {
          const qc = [...q.word].map((qch, k) => ({
            r: q.dir === "across" ? q.row : q.row + k,
            c: q.dir === "across" ? q.col + k : q.col, ch: qch,
          }));
          return qc.some((o) => o.r === cell.r && o.c === cell.c && o.ch !== cell.ch);
        }));
        if (conflict) continue;
        placed.push({ ...p, word: w, ...cand });
        done = true;
      }
      if (done) break;
    }
  }
  if (placed.length < 2) return null;
  const cells = {};
  placed.forEach((p, n) => {
    [...p.word].forEach((ch, k) => {
      const r = p.dir === "across" ? p.row : p.row + k;
      const c = p.dir === "across" ? p.col + k : p.col;
      cells[`${r}-${c}`] = { ch, words: [...(cells[`${r}-${c}`]?.words || []), n] };
    });
    p.n = n + 1;
  });
  const rows = Object.keys(cells).map((k) => +k.split("-")[0]);
  const cols = Object.keys(cells).map((k) => +k.split("-")[1]);
  const minR = Math.min(...rows), minC = Math.min(...cols);
  const grid = {};
  Object.entries(cells).forEach(([k, v]) => {
    const [r, c] = k.split("-").map(Number);
    grid[`${r - minR}-${c - minC}`] = v;
  });
  const clues = placed.map((p) => ({
    n: p.n, ar: p.ar, word: p.word, dir: p.dir,
    row: p.row - minR, col: p.col - minC,
  }));
  return { grid, clues, rows: Math.max(...rows) - minR + 1, cols: Math.max(...cols) - minC + 1 };
};

const Crossword = ({ payload, words, onDone }) => {  const data = useMemo(() => buildCrossword(
    (payload?.entries || []).filter((e) => e.ar && e.word)
      .map((e) => ({ word: e.word, ar: e.ar }))), [payload]);
  const [filled, setFilled] = useState({});
  const [active, setActive] = useState(0);
  const [solved, setSolved] = useState([]);

  if (!data || data.clues.length < 3) return <Match words={words} onDone={onDone} />;
  const clue = data.clues[active];

  const cellsOf = (c) => [...c.word].map((ch, k) => ({
    key: c.dir === "across" ? `${c.row}-${c.col + k}` : `${c.row + k}-${c.col}`, ch,
  }));

  const type = (key, val) => {
    const next = { ...filled, [key]: val.slice(-1).toLowerCase() };
    setFilled(next);
    const done = data.clues.filter((c) => cellsOf(c).every((x) => next[x.key] === x.ch));
    if (done.length !== solved.length) {
      const fresh = done.find((c) => !solved.includes(c.n));
      if (fresh) { sfx.correct(); say(fresh.word); }
      setSolved(done.map((c) => c.n));
      if (done.length === data.clues.length) {
        setTimeout(() => onDone(done.length, data.clues.length), 700);
      }
    }
  };

  return (
    <div data-testid="task-crossword">
      <div className="rounded-2xl p-3 mb-4" style={{ background: "rgba(255,255,255,.08)" }}>
        <div className="text-xs text-white/60">اضغط على أي رقم لتقرأ سؤاله، ثم اكتب الحروف</div>
        <div className="text-lg font-black mt-1" data-testid="crossword-clue">
          {clue.n}. {clue.ar} <span className="text-xs text-white/50">
            ({clue.dir === "across" ? "أفقي" : "رأسي"} · {clue.word.length} حروف)</span>
        </div>
      </div>
      <div className="overflow-x-auto pb-2">
        <div className="inline-grid gap-1"
          style={{ gridTemplateColumns: `repeat(${data.cols}, minmax(34px, 40px))` }}>
          {Array.from({ length: data.rows * data.cols }).map((_, idx) => {
            const r = Math.floor(idx / data.cols), c = idx % data.cols;
            const key = `${r}-${c}`;
            const cell = data.grid[key];
            if (!cell) return <div key={key} />;
            const num = data.clues.find((q) => q.row === r && q.col === c);
            const inActive = cellsOf(clue).some((x) => x.key === key);
            const ok = filled[key] === cell.ch;
            return (
              <div key={key} className="relative">
                {num && <span className="absolute top-0 start-0.5 text-[9px] text-[#05122e] font-black z-10">
                  {num.n}</span>}
                <input value={filled[key] || ""} data-testid={`cw-cell-${key}`}
                  onChange={(e) => type(key, e.target.value)}
                  onFocus={() => {
                    const owner = data.clues.findIndex((q) => cellsOf(q).some((x) => x.key === key));
                    if (owner >= 0) setActive(owner);
                  }}
                  className="w-full aspect-square rounded-md text-center font-black uppercase
                    outline-none border-2"
                  style={{
                    background: ok ? "#c8f7d4" : "#ffffff", color: "#05122e",
                    borderColor: inActive ? "var(--play-accent)" : "transparent",
                  }} />
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {data.clues.map((c, i) => (
          <button key={c.n} onClick={() => setActive(i)} data-testid={`cw-clue-${c.n}`}
            className="rounded-full px-3 py-1.5 text-xs font-black border"
            style={{
              background: solved.includes(c.n) ? "#3ec46d"
                : i === active ? "var(--play-accent)" : "rgba(255,255,255,.08)",
              borderColor: "rgba(255,255,255,.2)", color: "#fff",
            }}>{c.n}. {c.ar}</button>
        ))}
      </div>
    </div>
  );
};

// ---------- Picture/meaning word search: find every word in the letter grid ----------
const PicSearch = ({ payload, words, diff, onDone }) => {
  const size = Math.max(9, Math.min(diff?.huntSize || 9, 11));
  const targets = useMemo(() => (payload?.entries || [])
    .filter((e) => e.ar && /^[a-zA-Z]{3,8}$/.test(e.word) && e.word.length <= size)
    .slice(0, (diff?.grade || 3) <= 6 ? 3 : 4)
    .map((e) => ({ word: e.word.toLowerCase(), ar: e.ar })), [payload, diff, size]);

  // each word is placed only where every cell is still free, so nothing can be overwritten
  const { board, spots } = useMemo(() => {
    const g = Array.from({ length: size }, () => Array(size).fill(""));
    const spot = {};
    const free = (cells) => cells.every(([r, c]) => !g[r][c]);
    const usedRows = new Set();
    targets.forEach((t, i) => {
      const tries = [];
      for (let a = 0; a < 40; a++) {
        const across = i % 2 === 0 ? a < 30 : a >= 10;
        const lane = Math.floor(Math.random() * size);
        const start = Math.floor(Math.random() * (size - t.word.length + 1));
        tries.push([...t.word].map((_, k) => (across ? [lane, start + k] : [start + k, lane])));
      }
      let cells = tries.find(free);
      if (!cells) {
        const row = [...Array(size).keys()].find((r) => !usedRows.has(r)
          && g[r].every((x) => !x)) ?? i;
        usedRows.add(row);
        cells = [...t.word].map((_, k) => [row, k]);
      }
      cells.forEach(([r, c], k) => { g[r][c] = t.word[k]; });
      spot[t.word] = cells.map(([r, c]) => `${r}-${c}`);
    });
    const letters = "abcdefghijklmnopqrstuvwxyz";
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (!g[r][c]) g[r][c] = letters[Math.floor(Math.random() * 26)];
      }
    }
    return { board: g, spots: spot };
  }, [targets, size]);

  const [picked, setPicked] = useState([]);
  const [found, setFound] = useState([]);
  const foundCells = found.flatMap((w) => spots[w] || []);

  const tap = (r, c) => {
    const key = `${r}-${c}`;
    if (picked.includes(key)) return setPicked(picked.filter((k) => k !== key));
    const next = [...picked, key];
    const guess = next.map((k) => {
      const [rr, cc] = k.split("-").map(Number);
      return board[rr][cc];
    }).join("");
    const hit = targets.find((t) => t.word === guess && !found.includes(t.word));
    if (hit) {
      sfx.correct(); say(hit.word);
      const nf = [...found, hit.word];
      setFound(nf); setPicked([]);
      if (nf.length === targets.length) setTimeout(() => onDone(nf.length, targets.length), 600);
      return;
    }
    const possible = targets.some((t) => !found.includes(t.word) && t.word.startsWith(guess));
    if (!possible) { sfx.wrong(); return setPicked([]); }
    setPicked(next);
  };

  if (targets.length < 2) return <Hunt words={words} diff={diff} onDone={onDone} />;

  return (
    <div data-testid="task-picsearch">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs text-white/60">اضغط الحروف بالترتيب لتجد كل كلمة من هذه المعاني</p>
        {!!picked.length && (
          <button onClick={() => setPicked([])} data-testid="ps-reset"
            className="text-xs text-white/70 underline shrink-0">إلغاء التحديد</button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {targets.map((t) => (
          <span key={t.word} data-testid={`ps-clue-${t.word}`}
            className="rounded-xl px-3 py-2 text-xs font-black"
            style={{
              background: found.includes(t.word) ? "#3ec46d" : "rgba(255,255,255,.1)",
              color: "#fff", textDecoration: found.includes(t.word) ? "line-through" : "none",
            }}>{t.ar} ({t.word.length})</span>
        ))}
      </div>
      <div className="inline-grid gap-1"
        style={{ gridTemplateColumns: `repeat(${size}, minmax(28px, 34px))` }}>
        {board.map((row, r) => row.map((ch, c) => {
          const key = `${r}-${c}`;
          const isPicked = picked.includes(key);
          const isFound = foundCells.includes(key);
          return (
            <button key={key} onClick={() => tap(r, c)} data-testid={`ps-cell-${key}`}
              className="aspect-square rounded-md font-black uppercase text-sm"
              style={{
                background: isPicked ? "var(--play-accent)" : isFound ? "#3ec46d" : "rgba(255,255,255,.9)",
                color: isPicked ? "#fff" : "#05122e",
              }}>{ch}</button>
          );
        }))}
      </div>
    </div>
  );
};

// ---------- Sentence train: tap the carriages in the right order ----------
const Train = ({ payload, words, diff, onDone }) => {
  const rounds = useMemo(() => {
    const nouns = (words || []).filter((w) => /^[a-zA-Z]{3,10}$/.test(w));
    const noun = (k, fallback) => nouns[k] || fallback;
    const easy = [
      `The ${noun(0, "cat")} is very small`,
      `I like my new ${noun(1, "book")}`,
      `We play in the ${noun(2, "park")} every day`,
      `My sister has a red ${noun(3, "bag")}`,
    ];
    const hard = [
      "My friend and I read an interesting story after school",
      "The teacher explained the new lesson to all the students",
      "If it rains tomorrow we will stay at home and study",
      "She has been learning English for more than three years",
      "Although the exam was difficult most students passed it",
    ];
    const pool = (diff?.grade || 3) <= 5 ? easy : hard;
    return shuffle(pool).slice(0, diff?.builderRounds || 3);
  }, [words, diff]);

  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const target = rounds[i] || "";
  const parts = useMemo(() => shuffle(target.split(" ")), [target]);
  const [picked, setPicked] = useState([]);

  const take = (word, idx) => {
    const next = [...picked, { word, idx }];
    setPicked(next);
    if (next.length === target.split(" ").length) {
      const ok = next.map((x) => x.word).join(" ") === target;
      if (ok) { sfx.correct(); say(target); setScore((s) => s + 1); }
      else sfx.wrong();
      setTimeout(() => {
        if (i + 1 < rounds.length) { setI(i + 1); setPicked([]); }
        else onDone(score + (ok ? 1 : 0), rounds.length);
      }, 900);
    }
  };

  return (
    <div data-testid="task-train">
      <Progress i={i} total={rounds.length} score={score} />
      <p className="text-xs text-white/60 mb-3">رتّب عربات القطار لتصنع جملة صحيحة 🚂</p>
      <div className="flex flex-wrap items-center gap-2 rounded-2xl p-3 min-h-[64px]"
        style={{ background: "rgba(255,255,255,.08)" }} data-testid="train-line" dir="ltr">
        <span className="text-2xl">🚂</span>
        {picked.map((p, k) => (
          <span key={k} className="rounded-xl px-3 py-2 font-black text-sm"
            style={{ background: "var(--play-primary)", color: "#fff" }}>{p.word}</span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-4" dir="ltr">
        {parts.map((w, idx) => picked.some((p) => p.idx === idx) ? null : (
          <button key={idx} onClick={() => take(w, idx)} data-testid={`train-part-${idx}`}
            className="rounded-xl px-3 py-2 font-black text-sm"
            style={{ background: "rgba(255,255,255,.9)", color: "#05122e" }}>{w}</button>
        ))}
      </div>
      {picked.length > 0 && (
        <button onClick={() => setPicked([])} data-testid="train-reset"
          className="text-xs text-white/60 mt-3 underline">إعادة الترتيب</button>
      )}
    </div>
  );
};

export const CrosswordGame = Crossword;
export const PicSearchGame = PicSearch;
export const TrainGame = Train;

export const TASK_META = {
  match: { label: "مطابقة الحروف", emoji: "🔤", color: "var(--play-secondary)" },
  missing: { label: "الحرف الناقص", emoji: "🧩", color: "var(--play-primary)" },
  memory: { label: "الذاكرة", emoji: "🃏", color: "#635BFF" },
  spell: { label: "اكتب ما تسمع", emoji: "🎧", color: "#FF6B6B" },
  listening: { label: "استمع واختر", emoji: "🔊", color: "#00A6A6" },
  meanings: { label: "معاني الكلمات", emoji: "📖", color: "#38B000" },
  miniquiz: { label: "٣ أسئلة سريعة", emoji: "⚡", color: "var(--play-accent)" },
  reading: { label: "نص وأسئلة", emoji: "📜", color: "#c96a00" },
  hunt: { label: "صيد الكلمات", emoji: "🔍", color: "#1e63c8" },
  builder: { label: "بناء الجملة", emoji: "🏗️", color: "#8e44ad" },
  crossword: { label: "تقاطع الكلمات", emoji: "🧩", color: "#1b9aaa" },
  picsearch: { label: "ابحث عن الكلمات", emoji: "🔎", color: "#e07a5f" },
  train: { label: "قطار الجُمل", emoji: "🚂", color: "#f2545b" },
};

const COMPONENTS = { match: Match, missing: Missing, memory: Memory, spell: Spell,
  listening: Listening, meanings: Meanings, miniquiz: MiniQuiz, reading: Reading,
  hunt: Hunt, builder: Builder, crossword: Crossword, picsearch: PicSearch, train: Train };

export const effectiveTask = (task, payload) => {
  const words = (payload?.words || []).filter((w) => w && w.length > 1);
  const entries = (payload?.entries || []).filter((e) => e?.ar);
  const questions = (payload?.questions || []).filter((q) => Array.isArray(q?.options));
  if (task === "meanings" && entries.length < 2) return "match";
  if (task === "listening" && entries.length < 2) return "missing";
  if (task === "miniquiz" && !questions.length) return "match";
  if ((task === "crossword" || task === "picsearch") && entries.length < 3) return "match";
  if (task === "reading" && (!payload?.text || !questions.length)) return "match";
  if (!words.length && ["match", "missing", "memory", "spell", "hunt", "builder"].includes(task)) {
    return task;
  }
  return task;
};

export const TaskRunner = ({ task, payload, grade, stage, onDone }) => {
  const Comp = COMPONENTS[task] || Match;
  const diff = gameDiff(grade, stage);
  const words = (payload?.words || []).filter((w) => w && w.length > 1);
  return <Comp words={words.length >= 4 ? words : [...words, "book", "school", "friend", "day"]}
    payload={payload} diff={diff} onDone={onDone} />;
};
